import styled from "styled-components";
import { designTokens, getTypography } from "styles/designTokens";

export const PlayerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${designTokens.colors.line.button};
`;

export const TimelineControlWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const TimeDisplay = styled.span`
  font-size: 14px;
  color: ${designTokens.colors.font.tertiary};
  margin-left: auto;
`;

export const ScrollableContainer = styled.div`
  width: 100%;
  height: 35px;
  padding: 0 10px;
  overflow-x: auto;
  overflow-y: visible;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const TimelineContainer = styled.div`
  position: relative;
  height: 30px;
  cursor: pointer;
`;

export const TimelineBar = styled.div`
  position: absolute;
  top: 100%;
  transform: translateY(-50%);
  width: 100%;
  height: 4px;
  background-color: ${designTokens.colors.line.section};
`;

export const ProgressFill = styled.div`
  position: absolute;
  top: 100%;
  transform: translateY(-50%);
  height: 4px;
  background-color: ${designTokens.colors.font.secondary};
  z-index: 3;
`;

export const ProgressThumb = styled.div`
  position: absolute;
  top: 100%;
  width: 8px;
  height: 8px;
  background-color: ${designTokens.colors.font.secondary};
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 4;
`;

export const HighlightBar = styled.div`
  position: absolute;
  top: 100%;
  transform: translateY(-50%);
  height: 4px;
  background: #ff23bd;
  z-index: 2;
`;

export const MarkerWrapper = styled.div`
  position: relative;
  height: 28px;
  bottom: 0px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
`;

export const MarkerContainer = styled.div.attrs<{
  position: number;
  positionAlign: "start" | "center" | "end";
}>(({ position, positionAlign }) => ({
  style: {
    left: `${position}%`,
    transform:
      positionAlign === "start"
        ? "translateX(0)"
        : positionAlign === "end"
        ? "translateX(-100%)"
        : "translateX(-50%)",
  },
}))<{
  position: number;
  positionAlign: "start" | "center" | "end";
  contentAlign: "start" | "center" | "end";
}>`
  position: absolute;
  display: flex;
  flex-direction: column-reverse;

  align-items: ${({ contentAlign }) =>
    contentAlign === "start"
      ? "flex-start"
      : contentAlign === "end"
      ? "flex-end"
      : "center"};

  svg {
    position: relative;
    top: 5px;

    ${({ positionAlign }) => positionAlign === "start" && "right: 7.5px;"}
    ${({ positionAlign }) => positionAlign === "end" && "left: 7.5px;"}
  }
`;

export const MarkerLine = styled.div<{
  type: "loudness" | "pitch" | "emotion" | "speed" | "highlight" | "disabled";
  align?: "start" | "end";
}>`
  width: 1px;
  height: 20px;
  background-color: ${({ type }) => {
    switch (type) {
      case "emotion":
        return "#467FF2";
      case "loudness":
        return "#1B2C4F";
      case "speed":
        return "#2F4D9C";
      case "pitch":
        return "#6A7BEF";
      case "highlight":
        return "#FF23BD";
      case "disabled":
        return designTokens.colors.font.quinary;
      default:
        return "#A6A9F1"; // fallback color
    }
  }};
`;

export const CircleMarker = styled.div<{
  type: "loudness" | "pitch" | "emotion" | "speed" | "highlight" | "disabled";
}>`
  position: relative;
  top: 3px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ type }) => {
    switch (type) {
      case "emotion":
        return "#467FF2";
      case "loudness":
        return "#1B2C4F";
      case "speed":
        return "#2F4D9C";
      case "pitch":
        return "#6A7BEF";
      case "highlight":
        return "transparent";
      case "disabled":
        return designTokens.colors.font.quinary;
      default:
        return "#A6A9F1"; // fallback color
    }
  }};
`;

export const PlayButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${designTokens.colors.font.secondary};
  font-size: 16px;
`;

export const UtteranceDisplay = styled.div`
  background-color: ${designTokens.colors.box.primary};
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  align-items: flex-start;
  gap: 20px;
`;

export const Timestamp = styled.span`
  ${getTypography("korean", "body4")}
  color: ${designTokens.colors.font.tertiary};
  white-space: nowrap;
  height: 26px;
  display: flex;
  align-items: center;
`;

export const UtteranceText = styled.p`
  margin: 0;
  ${getTypography("korean", "body2")}
  color: ${designTokens.colors.font.tertiary};
`;

export const DefaultText = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: ${designTokens.colors.font.tertiary};
  ${getTypography("korean", "body3")}
`;
